import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Pencil, Search, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_STYLE = {
  attivo: "text-emerald-700 border-emerald-700/40",
  disiscritto: "text-amber-700 border-amber-700/40",
  archiviato: "text-taupe border-hair",
};

const btn = "min-h-[40px] inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border transition-colors disabled:opacity-50";

export function NewsletterContacts() {
  const [items, setItems] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("tutti");
  const [fConsent, setFConsent] = useState("tutti");
  const [editing, setEditing] = useState(null); // contatto in modifica
  const [form, setForm] = useState({ name: "", source: "", marketing_consent: true });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/newsletter?include_archived=${includeArchived}`);
      setItems(data);
    } catch { toast.error("Errore caricamento contatti newsletter"); }
  }, [includeArchived]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let out = items || [];
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((n) => (n.email || "").toLowerCase().includes(q) || (n.name || "").toLowerCase().includes(q));
    if (fStatus !== "tutti") out = out.filter((n) => n.status === fStatus);
    if (fConsent === "si") out = out.filter((n) => n.marketing_consent);
    if (fConsent === "no") out = out.filter((n) => !n.marketing_consent);
    return out;
  }, [items, search, fStatus, fConsent]);

  const openEdit = (n) => {
    setForm({ name: n.name || "", source: n.source || "", marketing_consent: !!n.marketing_consent });
    setEditing(n);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/newsletter/${editing.id}`, form);
      toast.success("Contatto aggiornato");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const toggleArchive = async (n) => {
    try {
      await api.post(`/admin/newsletter/${n.id}/${n.archived ? "unarchive" : "archive"}`);
      toast.success(n.archived ? "Contatto ripristinato" : "Contatto archiviato (storico preservato)");
      load();
    } catch { toast.error("Operazione non riuscita"); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Eliminare definitivamente ${n.email}?\n\nL'iscrizione newsletter verrà rimossa e il contatto non riceverà più comunicazioni marketing. L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/admin/newsletter/${n.id}`);
      toast.success("Contatto eliminato");
      load();
    } catch { toast.error("Eliminazione non riuscita"); }
  };

  const sel = "h-11 border border-hair bg-white px-3 text-sm rounded-none";

  return (
    <div data-testid="newsletter-contacts">
      {/* Ricerca e filtri */}
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per email o nome…" className="rounded-none pl-9" data-testid="nl-search" />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={sel} data-testid="nl-filter-status">
          <option value="tutti">Tutti gli stati</option>
          <option value="attivo">Attivi</option>
          <option value="disiscritto">Disiscritti</option>
          <option value="archiviato">Archiviati</option>
        </select>
        <select value={fConsent} onChange={(e) => setFConsent(e.target.value)} className={sel} data-testid="nl-filter-consent">
          <option value="tutti">Consenso: tutti</option>
          <option value="si">Consenso marketing: sì</option>
          <option value="no">Consenso marketing: no</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-taupe min-h-[44px] cursor-pointer">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="nl-show-archived" /> Mostra archiviati
        </label>
      </div>

      {!items ? <p className="text-taupe text-sm">Caricamento…</p> : (
        <div className="bg-white border border-hair overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="border-b border-hair text-left">
              <tr>
                <th className="p-4 font-normal eyebrow">Email</th>
                <th className="p-4 font-normal eyebrow">Nome</th>
                <th className="p-4 font-normal eyebrow">Sorgente</th>
                <th className="p-4 font-normal eyebrow">Iscritto il</th>
                <th className="p-4 font-normal eyebrow">Consenso</th>
                <th className="p-4 font-normal eyebrow">Stato</th>
                <th className="p-4 font-normal eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-taupe">Nessun contatto trovato.</td></tr>}
              {filtered.map((n) => (
                <tr key={n.id} className="border-b border-hair last:border-0" data-testid={`nl-row-${n.email}`}>
                  <td className="p-4 font-mono text-xs break-all">{n.email}</td>
                  <td className="p-4">{n.name || <span className="text-taupe">—</span>}</td>
                  <td className="p-4 text-taupe">{n.source || "—"}</td>
                  <td className="p-4 text-taupe text-xs">{fmtDate(n.subscribed_at)}</td>
                  <td className="p-4" data-testid={`nl-consent-${n.email}`}>{n.marketing_consent ? <span className="text-emerald-700">Sì</span> : <span className="text-red-700">No</span>}</td>
                  <td className="p-4"><span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${STATUS_STYLE[n.status] || ""}`}>{n.status}</span></td>
                  <td className="p-4">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(n)} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid={`nl-edit-${n.email}`}><Pencil size={13} /> Modifica</button>
                      <button onClick={() => toggleArchive(n)} className={`${btn} border-hair hover:border-ink`} data-testid={`nl-archive-${n.email}`}>
                        {n.archived ? <><ArchiveRestore size={13} /> Ripristina</> : <><Archive size={13} /> Archivia</>}
                      </button>
                      <button onClick={() => remove(n)} className={`${btn} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`nl-delete-${n.email}`}><Trash2 size={13} /> Elimina</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale modifica */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none" data-testid="nl-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Modifica contatto</DialogTitle></DialogHeader>
          <p className="text-xs text-taupe font-mono break-all">{editing?.email}</p>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="nl-edit-name" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Sorgente</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="rounded-none mt-1" data-testid="nl-edit-source" />
            </div>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: v })} data-testid="nl-edit-consent" />
              <span>Consenso marketing {form.marketing_consent ? "" : "(non riceverà più comunicazioni)"}</span>
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setEditing(null)} className={`${btn} border-hair hover:border-ink`}>Annulla</button>
              <button onClick={saveEdit} disabled={busy} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink`} data-testid="nl-edit-save">{busy ? "Salvataggio…" : "Salva"}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
